import React, { useContext, useState } from 'react';
import { OverlayPanel } from 'primereact/overlaypanel';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import { confirmDialog } from 'primereact/confirmdialog';
import { updateTimeEntry, createTimeEntry } from 'services/timerServices';
import { toastConstant } from 'constants/toastmessage';
import { getTenantDetails } from 'utils/utils';
import { UserDetailsContext } from 'context/userDetailsContext';
import { convertToTimezoneWithStartOfDayFormatWithCurrentTime } from 'utils/utility_functions/timezone';
import { useQueryClient } from '@tanstack/react-query';
import { queryKeys } from 'modules/BillingV2/config/queryClient';
import { useCaseLeadListQuery } from 'hooks/queries/useCaseLeadListQuery';

const TimerOverlay = ({
  timerCaseOverlay,
  showTimerManagement,
  setShowTimerManagement,
  timerList,
  setTimerList,
  loadTimer,
  caseSearchValue,
  setCaseSearchValue,
  billingCodes,
  onChangeNotes,
  onChangeBillingCode,
  billingCodeOptionTemplate,
  billingCodeValueTemplate,
  getTimerDisplay,
  getRunningTimersCount,
  getPausedTimersCount,
  getTotalActiveTimersCount,
  handleDeleteTimer,
  handleStartNewTimer,
  addToast,
}) => {
  const [loadingTimer, setLoadingTimer] = useState(false);
  const [tenantId, tenantSk] = getTenantDetails();
  const userContext = useContext(UserDetailsContext);
  const queryClient = useQueryClient();

  // Use React Query for case-lead list with caching
  const {
    casesList,
    isFetching: loadingCases,
    handleSearch: debouncedLoadCasesForTimer,
    resetSearch: resetCaseSearch,
  } = useCaseLeadListQuery({
    enabled: !!tenantId && !!tenantSk,
  });

  const getBillingCodesForTimer = (timer) => {
    if (!timer || !timer.sk || !billingCodes || billingCodes.length === 0) return billingCodes;

    const practiceArea = timer?.case_practice_area || null;

    if (!practiceArea) {
      return billingCodes;
    }

    const filteredCodes = billingCodes?.filter((code) => {
      if (!code.practice_area_ids || code.practice_area_ids.length === 0) {
        return true;
      }
      if (code.practice_area_ids.includes('ALL')) {
        return true;
      }
      if (code.practice_area_ids.includes(practiceArea)) {
        return true;
      }
      return false;
    });

    return filteredCodes?.length > 0 ? filteredCodes : billingCodes;
  };

  const handleCaseSearch = (e) => {
    const value = e.target.value;
    setCaseSearchValue(value);
    debouncedLoadCasesForTimer(value);
  };

  const handleCaseSelect = (selectedCase) => {
    setLoadingTimer(true);
    createTimerForCase(selectedCase);
  };

  const createTimerForCase = (caseInput) => {
    // Auto-pause any currently running timers
    const currentlyRunning = timerList.filter((t) => t.start_time && !t.end_time && t.time_entry_status !== 'COMPLETED');

    if (currentlyRunning.length > 0) {
      // Update local state immediately to reflect paused timers and avoid showing multiple running timers
      let newTimerListState = [...timerList];
      currentlyRunning.forEach((runningTimer) => {
        const now = new Date().getTime();
        let updatedTimer = { ...runningTimer };

        // Set hour_rate before pausing to prevent 400 error
        updatedTimer.hour_rate = userContext?.profileData?.rate || '0.00';
        updatedTimer.end_time = now;

        let previousMs = 0;
        if (runningTimer.total_times && runningTimer.total_times !== '00:00:00') {
          const [h, m, s] = runningTimer.total_times.split(':').map(Number);
          previousMs = ((h || 0) * 3600 + (m || 0) * 60 + (s || 0)) * 1000;
        } else if (runningTimer.total_hours && parseFloat(runningTimer.total_hours) > 0) {
          previousMs = parseFloat(runningTimer.total_hours) * 3600 * 1000;
        }

        const elapsed = runningTimer.start_time ? now - runningTimer.start_time : 0;
        const totalMs = previousMs + elapsed;

        const totalSeconds = Math.floor(totalMs / 1000);
        const hours = Math.floor(totalSeconds / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const seconds = totalSeconds % 60;
        updatedTimer.total_times = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds
          .toString()
          .padStart(2, '0')}`;
        updatedTimer.total_hours = (totalMs / (3600 * 1000)).toFixed(1);
        updatedTimer.start_time = '';
        // Update time_entry_date to current time with proper timezone
        updatedTimer.time_entry_date = convertToTimezoneWithStartOfDayFormatWithCurrentTime(new Date(), userContext?.userDetails?.timezone);
        // Update local state
        newTimerListState = newTimerListState.map((t) => (t.sk === runningTimer.sk ? updatedTimer : t));
        updateTimeEntry(updatedTimer)
          .then(() => {
            queryClient.invalidateQueries({ queryKey: queryKeys.timeEntries.all });
            loadTimer(); // Invalidate timer-list React Query cache
          })
          .catch(() => loadTimer());
      });
      setTimerList(newTimerListState);
    }

    // Determine target case
    const caseId = typeof caseInput === 'string' ? caseInput : caseInput?.case_id;
    const caseName = typeof caseInput === 'string' ? '' : caseInput?.case_description?.case_name || caseInput?.case_name || '';

    // If an existing non-completed timer for this case exists, resume it instead of creating new
    const normalized = (s) => (s || '').toString().trim().toLowerCase();
    const existingTimer = (timerList || []).find(
      (t) =>
        t.time_entry_status !== 'COMPLETED' &&
        ((t.case_id && caseId && t.case_id === caseId) || (!t.case_id && caseName && normalized(t.case_name) === normalized(caseName)))
    );

    if (existingTimer) {
      // Resume the existing timer (this will also pause others per resume logic)
      handleTimerAction(existingTimer, 'resume');
      // Keep overlay open and switch to management view
      setShowTimerManagement(true);
      addToast(false, toastConstant.toasterType.SUCCESS, toastConstant.api.SUCCESS, 'Resumed existing timer');
      setLoadingTimer(false);
      return;
    }
    // Create new timer
    let data = {
      case_id: caseId,
      start_time: new Date().getTime(),
      time_entry_status: 'PROGRESS',
      billable: true,
      hour_rate: '',
      total_times: '00:00:00',
      total_hours: '0',
    };
    if (caseName) {
      data.case_name = caseName;
    }

    data.time_entry_date = convertToTimezoneWithStartOfDayFormatWithCurrentTime(new Date(), userContext?.userDetails?.timezone);

    const entry = { ...data };
    // Ensure created timer matches list filter (PROGRESS) so it appears immediately
    const payload = { time_entry_status: 'PROGRESS', time_entries: [entry] };
    createTimeEntry(payload)
      .then(() => {
        // Invalidate time entries queries to refresh the list
        queryClient.invalidateQueries({ queryKey: queryKeys.timeEntries.all });
        loadTimer();
        // Keep overlay open and switch to management view
        setShowTimerManagement(true);
        addToast(false, toastConstant.toasterType.SUCCESS, toastConstant.api.SUCCESS, 'Timer created successfully');
      })
      .catch((error) => {
        console.error('Error creating timer:', error);
        addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.ERROR, 'Failed to create timer');
      })
      .finally(() => {
        setLoadingTimer(false);
      });
  };

  const handleTimerAction = async (timer, action) => {
    if (action === 'pause') {
      timer.hour_rate = userContext?.profileData?.rate || '0.00';
      // Pause timer
      let updatedTimer = { ...timer };
      const now = new Date().getTime();
      updatedTimer.end_time = now;

      let previousMs = 0;
      if (timer.total_times && timer.total_times !== '00:00:00') {
        const [h, m, s] = timer.total_times.split(':').map(Number);
        previousMs = ((h || 0) * 3600 + (m || 0) * 60 + (s || 0)) * 1000;
      } else if (timer.total_hours && parseFloat(timer.total_hours) > 0) {
        previousMs = parseFloat(timer.total_hours) * 3600 * 1000;
      }

      let elapsed = 0;
      if (timer.start_time) {
        elapsed = now - timer.start_time;
      }
      const totalMs = previousMs + elapsed;

      const totalSeconds = Math.floor(totalMs / 1000);
      const hours = Math.floor(totalSeconds / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;
      updatedTimer.total_times = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds
        .toString()
        .padStart(2, '0')}`;
      updatedTimer.total_hours = (totalMs / (3600 * 1000)).toFixed(1);
      updatedTimer.start_time = '';

      // Update time_entry_date to current time with proper timezone
      updatedTimer.time_entry_date = convertToTimezoneWithStartOfDayFormatWithCurrentTime(new Date(), userContext?.userDetails?.timezone);

      const updatedTimerList = timerList.map((t) => (t.sk === timer.sk ? updatedTimer : t));
      setTimerList(updatedTimerList);

      updateTimeEntry(updatedTimer)
        .then(() => {
          queryClient.invalidateQueries({ queryKey: queryKeys.timeEntries.all });
          loadTimer(); // Invalidate timer-list React Query cache
        })
        .catch(() => {
          loadTimer();
        });
    } else if (action === 'resume') {
      // Ensure only one timer is active: pause any other running timers before resuming this one
      const now = new Date().getTime();

      // Pause all other running timers
      const runningOthers = timerList.filter(
        (t) => t.sk !== timer.sk && t.start_time && !t.end_time && t.time_entry_status !== 'COMPLETED'
      );

      let newTimerListState = [...timerList];
      if (runningOthers.length > 0) {
        runningOthers.forEach((runningTimer) => {
          let updatedRunning = { ...runningTimer };

          // Set hour_rate before pausing to prevent 400 error
          updatedRunning.hour_rate = userContext?.profileData?.rate || '0.00';
          updatedRunning.end_time = now;

          // Calculate total time up to the pause moment
          let previousMs = 0;
          if (runningTimer.total_times && runningTimer.total_times !== '00:00:00') {
            const [h, m, s] = runningTimer.total_times.split(':').map(Number);
            previousMs = ((h || 0) * 3600 + (m || 0) * 60 + (s || 0)) * 1000;
          } else if (runningTimer.total_hours && parseFloat(runningTimer.total_hours) > 0) {
            previousMs = parseFloat(runningTimer.total_hours) * 3600 * 1000;
          }

          const elapsed = runningTimer.start_time ? now - runningTimer.start_time : 0;
          const totalMs = previousMs + elapsed;

          const totalSeconds = Math.floor(totalMs / 1000);
          const hours = Math.floor(totalSeconds / 3600);
          const minutes = Math.floor((totalSeconds % 3600) / 60);
          const seconds = totalSeconds % 60;
          updatedRunning.total_times = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds
            .toString()
            .padStart(2, '0')}`;
          updatedRunning.total_hours = (totalMs / (3600 * 1000)).toFixed(1);
          updatedRunning.start_time = '';
          // Update time_entry_date to current time with proper timezone
          updatedRunning.time_entry_date = convertToTimezoneWithStartOfDayFormatWithCurrentTime(
            new Date(),
            userContext?.userDetails?.timezone
          );

          newTimerListState = newTimerListState.map((t) => (t.sk === runningTimer.sk ? updatedRunning : t));
          updateTimeEntry(updatedRunning)
            .then(() => {
              queryClient.invalidateQueries({ queryKey: queryKeys.timeEntries.all });
              loadTimer(); // Invalidate timer-list React Query cache
            })
            .catch(() => {
              loadTimer();
            });
        });
      }

      // Resume selected timer
      let updatedTimer = { ...timer };
      updatedTimer.start_time = now;
      updatedTimer.end_time = '';
      // Update time_entry_date to current time with proper timezone
      updatedTimer.time_entry_date = convertToTimezoneWithStartOfDayFormatWithCurrentTime(new Date(), userContext?.userDetails?.timezone);

      // Update time_entry_date to current time with proper timezone
      updatedTimer.time_entry_date = convertToTimezoneWithStartOfDayFormatWithCurrentTime(new Date(), userContext?.userDetails?.timezone);

      newTimerListState = newTimerListState.map((t) => (t.sk === timer.sk ? updatedTimer : t));
      setTimerList(newTimerListState);

      updateTimeEntry(updatedTimer)
        .then(() => {
          queryClient.invalidateQueries({ queryKey: queryKeys.timeEntries.all });
          loadTimer(); // Invalidate timer-list React Query cache
        })
        .catch(() => {
          loadTimer();
        });
    } else if (action === 'stop') {
      // Stop timer and update API (save time values with PROGRESS status, but show as READY_TO_SAVE locally)
      timer.hour_rate = userContext?.profileData?.rate || '0.00';
      let stoppedTimer = { ...timer };
      if (!timer.end_time) {
        const now = new Date().getTime();
        stoppedTimer.end_time = now;

        let previousMs = 0;
        if (timer.total_times && timer.total_times !== '00:00:00') {
          const [h, m, s] = timer.total_times.split(':').map(Number);
          previousMs = ((h || 0) * 3600 + (m || 0) * 60 + (s || 0)) * 1000;
        } else if (timer.total_hours && parseFloat(timer.total_hours) > 0) {
          previousMs = parseFloat(timer.total_hours) * 3600 * 1000;
        }

        let elapsed = 0;
        if (timer.start_time) {
          elapsed = now - timer.start_time;
        }
        const totalMs = previousMs + elapsed;

        const totalSeconds = Math.floor(totalMs / 1000);
        const hours = Math.floor(totalSeconds / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const seconds = totalSeconds % 60;
        stoppedTimer.total_times = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds
          .toString()
          .padStart(2, '0')}`;
        stoppedTimer.total_hours = (totalMs / (3600 * 1000)).toFixed(1);
        stoppedTimer.start_time = '';
      }

      // Update time_entry_date to current time with proper timezone
      stoppedTimer.time_entry_date = convertToTimezoneWithStartOfDayFormatWithCurrentTime(new Date(), userContext?.userDetails?.timezone);

      // Create a version for API with PROGRESS status (backend doesn't persist READY_TO_SAVE)
      const apiTimer = { ...stoppedTimer, time_entry_status: 'PROGRESS' };

      // Create a version for local state with READY_TO_SAVE status (for UI display)
      const localTimer = { ...stoppedTimer, time_entry_status: 'READY_TO_SAVE' };

      // Update local state with READY_TO_SAVE to show Save button
      const updatedTimerList = timerList.map((t) => (t.sk === timer.sk ? localTimer : t));
      setTimerList(updatedTimerList);

      // Call API to update the time values but keep status as PROGRESS
      updateTimeEntry(apiTimer)
        .then(() => {
          queryClient.invalidateQueries({ queryKey: queryKeys.timeEntries.all });
          loadTimer(); // Invalidate timer-list React Query cache
        })
        .catch(() => {
          loadTimer();
        });
    } else if (action === 'save') {
      // Finalize timer and call API
      setLoadingTimer(true);
      let savedTimer = { ...timer };
      if (!savedTimer.end_time) {
        const now = new Date().getTime();
        savedTimer.end_time = now;

        let previousMs = 0;
        if (savedTimer.total_times && savedTimer.total_times !== '00:00:00') {
          const [h, m, s] = savedTimer.total_times.split(':').map(Number);
          previousMs = ((h || 0) * 3600 + (m || 0) * 60 + (s || 0)) * 1000;
        } else if (savedTimer.total_hours && parseFloat(savedTimer.total_hours) > 0) {
          previousMs = parseFloat(savedTimer.total_hours) * 3600 * 1000;
        }

        let elapsed = 0;
        if (savedTimer.start_time) {
          elapsed = now - savedTimer.start_time;
        }
        const totalMs = previousMs + elapsed;

        const totalSeconds = Math.floor(totalMs / 1000);
        const hours = Math.floor(totalSeconds / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const seconds = totalSeconds % 60;
        savedTimer.total_times = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds
          .toString()
          .padStart(2, '0')}`;

        let calculatedHours = (totalMs / (3600 * 1000)).toFixed(1);
        if (calculatedHours < 0.01) {
          calculatedHours = 0.1;
        }
        savedTimer.total_hours = calculatedHours === 0.1 ? '0.1' : calculatedHours.toFixed(1);
        savedTimer.start_time = '';
      }

      if (savedTimer?.total_hours && parseFloat(savedTimer.total_hours) < 0.01) {
        savedTimer.total_hours = '0.1';
      }

      savedTimer.time_entry_status = 'OPEN';

      // Update time_entry_date to current time with proper timezone
      savedTimer.time_entry_date = convertToTimezoneWithStartOfDayFormatWithCurrentTime(new Date(), userContext?.userDetails?.timezone);

      // const hourRate = await getUserHourRate(savedTimer?.case_id, userContext?.userDetails?.firm_user_id);

      // savedTimer.hour_rate = hourRate?.data?.rate || '0.00';
      savedTimer.hour_rate = userContext?.profileData?.rate || '0.00';
      if (savedTimer.hour_rate !== '0.00') {
        savedTimer.amount = !isNaN(savedTimer.total_hours * savedTimer.hour_rate)
          ? (savedTimer.total_hours * savedTimer.hour_rate).toFixed(2)
          : '';
      }

      updateTimeEntry(savedTimer)
        .then(() => {
          // Invalidate time entries queries to refresh the list
          queryClient.invalidateQueries({ queryKey: queryKeys.timeEntries.all });
          loadTimer();
          addToast(false, toastConstant.toasterType.SUCCESS, toastConstant.api.SUCCESS, 'Timer saved successfully');
        })
        .catch((error) => {
          console.error('Error saving timer:', error);
          addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.ERROR, 'Failed to save timer');
          loadTimer();
        })
        .finally(() => {
          setLoadingTimer(false);
        });
    }
  };

  const confirmDeleteAction = (timer) => {
    confirmDialog({
      message: 'Are you sure you want to delete this timer?',
      header: 'Confirm Delete',
      icon: 'pi pi-exclamation-triangle',
      baseZIndex: 2000000,
      maskClassName: 'confirm-delete-mask',
      draggable: false,
      resizable: false,
      rejectClassName: 'p-button-secondary outline',
      style: {
        width: '400px',
        maxWidth: '400px',
        overflowX: 'hidden',
        zIndex: 2000000,
      },
      breakpoints: {
        '960px': '75vw',
        '640px': '90vw',
      },
      className: 'common-popup-style confirm-delete-popup',
      accept: () => {
        handleDeleteTimer(timer);
      },
      reject: () => null,
    });
  };

  return (
    <OverlayPanel
      ref={timerCaseOverlay}
      className="timer-case-overlay-panel"
      style={{ width: '450px', maxHeight: '600px', zIndex: 1000 }}
      baseZIndex={1000}
      appendTo={typeof document !== 'undefined' ? document.body : null}
      onHide={() => {
        setCaseSearchValue('');
        setShowTimerManagement(false);
        resetCaseSearch();
      }}
      dismissable
      closeOnEscape
    >
      <div className="timer-case-overlay-content pt-0 pb-0 mt-0 mb-0" style={{ position: 'relative' }}>
        {loadingTimer && (
          <div
            style={{
              position: 'absolute',
              zIndex: 1,
              height: '100%',
              width: '100%',
              background: '#00000059',
              paddingTop: '36%',
            }}
          >
            <div className="text-center p-3">
              <i className="pi pi-spin pi-spinner" style={{ fontSize: '1.5rem' }}></i>
            </div>
          </div>
        )}
        {!showTimerManagement ? (
          /* Case Selection Interface */
          <>
            {/* Search Bar */}
            <div className="mb-3">
              <span className="p-input-icon-left w-100">
                <i className="icon-search" />
                <InputText
                  value={caseSearchValue}
                  onChange={handleCaseSearch}
                  placeholder="Search and select a case ..."
                  className="w-100"
                  autoFocus
                />
              </span>
            </div>

            {/* Case List */}
            <div className="case-list-container" style={{ maxHeight: '300px', overflowY: 'auto' }}>
              {loadingCases ? (
                <div className="text-center p-3">
                  <i className="pi pi-spin pi-spinner" style={{ fontSize: '1.5rem' }}></i>
                  <div className="mt-2">Loading cases...</div>
                </div>
              ) : casesList.length > 0 ? (
                casesList.map((caseItem) => (
                  <div
                    key={caseItem.case_id}
                    className="case-item py-1 pe-1 cursor-pointer"
                    onClick={() => handleCaseSelect(caseItem)}
                    style={{ borderRadius: '4px' }}
                  >
                    <div className="case-name F-size14">{caseItem.case_description?.case_name || 'TBD'}</div>
                  </div>
                ))
              ) : (
                <div className="text-center p-3 text-muted F-size14">
                  {caseSearchValue ? 'No cases found matching your search' : 'No cases available'}
                </div>
              )}
            </div>
          </>
        ) : (
          /* Timer Management Interface */
          <>
            {/* Header */}
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <h6 className="sub-title m-0" style={{ fontSize: '16px', marginBottom: '4px' }}>
                  Active Timers
                </h6>
                <small className="text-muted" style={{ fontSize: '12px', color: 'var(--text-color-secondary)' }}>
                  {getRunningTimersCount()} running, {getPausedTimersCount()} paused
                </small>
              </div>
              <i
                className="pi pi-times cursor-pointer"
                style={{ fontSize: '16px', color: 'var(--text-color)', padding: '4px' }}
                onClick={() => timerCaseOverlay.current?.hide()}
              ></i>
            </div>

            {/* Divider under Active Timers header */}
            <div
              style={{
                height: '1px',
                width: '100%',
                backgroundColor: 'var(--input-border)',
                margin: '6px 0 10px 0',
                borderRadius: '0',
              }}
            />

            {/* Timer List */}
            <div className="timer-list-container" style={{ maxHeight: '400px', overflowY: 'auto', marginTop: '4px' }}>
              {timerList
                ?.filter((timer) => timer.time_entry_status !== 'COMPLETED')
                ?.map((timer, index) => (
                  <div
                    key={timer.sk}
                    className={`timer-item border rounded ${!timer.end_time ? 'running-timer' : 'paused-timer'} ${
                      timer.time_entry_status === 'READY_TO_SAVE' ? 'timer-ready-to-save' : ''
                    }`}
                  >
                    {' '}
                    {/* Timer Header - Case Name and Status */}
                    <div className="d-flex align-items-center mb-1">
                      <span
                        className={`timer-status-dot ${!timer.end_time ? 'running' : 'paused'}`}
                        style={{
                          width: '8px',
                          height: '8px',
                          borderRadius: '50%',
                          backgroundColor: !timer.end_time ? '#22c55e' : '#f59e0b',
                          display: 'inline-block',
                          marginRight: '8px',
                          flexShrink: 0,
                        }}
                      ></span>
                      <div className="flex-1">
                        <div className="d-flex align-items-center justify-content-between">
                          <span className="title-text timer-case-name">
                            {timer.case_name || 'TBD'} {timer.unique_number && `#${timer.unique_number}`}
                          </span>
                        </div>
                      </div>
                    </div>
                    {/* Divider between header and timer card content
                    <hr
                      style={{
                        margin: '2px 0 6px 0',
                        padding: '0',
                        border: 'none',
                        borderTop: '1px solid var(--input-border)',
                        borderRadius: '0',
                      }}
                    /> */}
                    {/* Timer Duration */}
                    <div className="mb-1">
                      <div className="timer-display text-bold" style={{ fontSize: '18px', lineHeight: '1.2', marginBottom: '2px' }}>
                        {getTimerDisplay(timer)}
                      </div>
                      <div style={{ fontSize: '12px', color: 'var(--text-color-secondary)' }}>
                        {(() => {
                          if (timer.start_time && !timer.end_time) {
                            const now = Date.now();
                            const previousTime = timer.total_hours ? parseFloat(timer.total_hours) * 3600 * 1000 : 0;
                            const elapsed = now - timer.start_time;
                            const totalMs = previousTime + elapsed;
                            return (totalMs / (3600 * 1000)).toFixed(1) + ' hrs';
                          } else {
                            const hoursValue = parseFloat(timer.total_hours || '0');
                            return (isNaN(hoursValue) ? '0.0' : hoursValue.toFixed(1)) + ' hrs';
                          }
                        })()}
                      </div>
                    </div>
                    {/* Divider after hrs summary */}
                    <div
                      style={{
                        height: '1px',
                        width: '100%',
                        backgroundColor: 'var(--input-border)',
                        margin: '6px 0 8px 0',
                      }}
                    />
                    {/* Description */}
                    <div className="mb-1">
                      <InputTextarea
                        key={`desc-${timer.sk}`}
                        placeholder="Add description"
                        value={timer.notes || ''}
                        onChange={(e) => onChangeNotes(e.target.value, timer.sk)}
                        className="w-100 time-entry-description"
                        rows={2}
                        style={{
                          fontSize: '14px',
                          lineHeight: '1',
                          border: '1px solid var(--input-border)',
                          borderRadius: '4px',
                          backgroundColor: 'var(--input-bg)',
                        }}
                        autoResize={false}
                      />
                    </div>
                    {/* Billing Code */}
                    <div className="mb-1 timer-billing-code">
                      <Dropdown
                        placeholder="Select billing code"
                        className="w-100 billing-code"
                        style={{ fontSize: '14px' }}
                        panelStyle={{ width: '420px', maxWidth: '420px', overflowX: 'hidden', zIndex: 2000000 }}
                        appendTo={typeof document !== 'undefined' ? document.body : null}
                        scrollHeight="260px"
                        options={getBillingCodesForTimer(timer)}
                        value={getBillingCodesForTimer(timer)?.find((code) => code.sk === timer.activity_type_sk) || null}
                        onChange={(e) => onChangeBillingCode(e.value, timer.sk)}
                        optionLabel="activity_type"
                        itemTemplate={billingCodeOptionTemplate}
                        valueTemplate={billingCodeValueTemplate}
                        showClear
                        filter
                        filterBy="activity_type,activity_type_code"
                        filterPlaceholder="Search billing codes..."
                        emptyFilterMessage="No billing codes found"
                      />
                    </div>
                    {/* Timer Actions */}
                    <div className="actions-row">
                      <Button
                        label={!timer.end_time ? 'Pause' : 'Resume'}
                        icon={!timer.end_time ? 'bi bi-pause-fill' : 'bi bi-play-fill'}
                        className="p-button-sm p-button-outlined p-button-primary add-button top-button"
                        onClick={() => handleTimerAction(timer, !timer.end_time ? 'pause' : 'resume')}
                        style={{ flex: 1 }}
                      />
                      {timer.time_entry_status === 'READY_TO_SAVE' ? (
                        <Button
                          label="Save"
                          icon="pi pi-check"
                          className="p-button-sm p-button-primary add-button top-button"
                          onClick={() => handleTimerAction(timer, 'save')}
                          style={{ flex: 1 }}
                        />
                      ) : (
                        <Button
                          label="Stop"
                          icon="bi bi-stop-fill"
                          className="p-button-sm p-button-outlined p-button-primary add-button top-button"
                          onClick={() => handleTimerAction(timer, 'stop')}
                          style={{ flex: 1 }}
                        />
                      )}
                      <Button
                        label="Delete"
                        icon="pi pi-trash"
                        className="p-button-sm p-button-outlined p-button-danger"
                        onClick={() => confirmDeleteAction(timer)}
                        style={{ flex: 1 }}
                      />
                    </div>
                  </div>
                ))}
            </div>

            {/* Start New Timer Button */}
            <div className="text-center mt-2 pt-1 d-flex justify-content-center" style={{ borderTop: '1px solid var(--input-border)' }}>
              <Button
                label="+ Start New Timer"
                className="p-button p-component p-button-outlined p-button-info top-button p-button-primary add-button w-50 mt-2"
                style={{
                  color: 'var(--primary-main)',
                  fontSize: '14px',
                  fontWeight: '500',
                  padding: '8px 16px',
                }}
                onClick={handleStartNewTimer}
                disabled={getTotalActiveTimersCount() >= 5}
              />
            </div>
          </>
        )}
      </div>
    </OverlayPanel>
  );
};

export default TimerOverlay;
